import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { getIntlLocale } from '../../utils/functions';
import { useTranslation } from 'react-i18next';
import Textarea from '../shared/TailwindComponents/Textarea';
import { FIELD_LABEL_CLASS } from '../shared/TailwindComponents/FieldShell';
import Badge from '../shared/TailwindComponents/Badge';
import { PiTrashLight, PiArrowLeftLight, PiUserLight, PiFileTextLight, PiQuestionMarkLight, PiUserPlusLight, PiCheckCircleLight, PiClockLight, PiChatTextLight, PiPencilSimpleLight, PiLockSimpleLight } from 'react-icons/pi';
import { addQuestionnaireItem, updateQuestionnaireItem, removeQuestionnaireItem, finalizeQuestionnaireItem, assignQuestionnaireItem, restoreQuestionnaireItem } from '../../store/actions/Matters/MatterActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { RootState, useAppDispatch } from '../../store';
import { getUserType } from '../../utils/authUtils';
import Button from '../shared/TailwindComponents/Button';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { useSelector } from 'react-redux';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, questionnairePanelTourSteps } from './FeatureTour';

interface QuestionnairePanelProps {
  matterId: string;
  questionIndex: number;
  item?: {
    question: string;
    potential_answer: string;
    status: 'pending' | 'answered';
    who_answered: string;
    when_answered: string;
    client_assigned: boolean;
    question_id?: string; 
  };
  onClose: () => void;
  onUpdate: () => void;
}

const QuestionnairePanel = forwardRef((props: QuestionnairePanelProps, ref) => {
  const { matterId, questionIndex, item, onClose, onUpdate } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAddMode = questionIndex === -1;
  
  const [question, setQuestion] = useState(item?.question || '');
  const [answer, setAnswer] = useState(item?.potential_answer || '');
  const [status, setStatus] = useState<'pending' | 'answered' | 'finalized'>(item?.status || 'pending');
  const [clientAssigned, setClientAssigned] = useState(item?.client_assigned || false);
  const userType = getUserType();
  const isLawyer = userType ? userType.includes('Law') : false;
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'questionnaire-panel',
    steps: questionnairePanelTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  const handleRestore = async () => {
    if (status !== 'finalized') return;
    
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Restore Question'),
      subMsg: t('This will move the question back to Answered status and allow further editing.'),
      onConfirm: async () => {
        try {
          const questionIdentifier = item?.question_id || questionIndex.toString();
          await dispatch(restoreQuestionnaireItem(matterId, questionIdentifier));
          dispatch(showFeedbackModal({ 
            showModal: true, 
            message: t('Question restored successfully'), 
            modalType: 'success', 
            duration: 3000 
          }));
          onUpdate();
          onClose();
        } catch (error) {
          if(!error) return
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Failed to restore question'),
            modalType: 'error',
            duration: 3000
          }));
        }
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      icon: <PiArrowLeftLight className="w-6 h-6 text-warning-600" />,
    }));
  };

  const handleSave = async () => {
    const currentStatus = answer.trim() && status === 'pending' ? 'answered' : status;
    setStatus(currentStatus);
    const updatedItem = {
      question,
      potential_answer: answer,
      status: currentStatus, 
      client_assigned: clientAssigned,
      who_answered: currentStatus === 'answered' ? `${userInfo.FirstName} ${userInfo.LastName}` : item?.who_answered || '',
      when_answered: currentStatus === 'answered' ? new Date().toISOString() : item?.when_answered || '',
    } as any; 

    try {
      if (isAddMode) {
        const result = await dispatch(addQuestionnaireItem(matterId, question));
        if (result) {
          dispatch(showFeedbackModal({ 
            showModal: true, 
            message: t('Question added'), 
            modalType: 'success', 
            duration: 3000 
          }));
          onUpdate();
          onClose();
        }
      } else {
        const questionIdentifier = item?.question_id || questionIndex.toString();
        const result = await dispatch(updateQuestionnaireItem(matterId, questionIdentifier, updatedItem));
        if (result) {
          // dispatch(showFeedbackModal({ 
          //   showModal: true, 
          //   message: t('Question updated'), 
          //   modalType: 'success', 
          //   duration: 3000 
          // }));
          onUpdate();
          onClose();
        }
      }
    } catch (error) {
      if(!error) return
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to save question'),
        modalType: 'error',
        duration: 3000
      }));
    }
  };

  const handleToggleStatus = async () => {
    if (status === 'pending') {
      const newStatus = 'answered';
      if (!answer.trim()) {
        dispatch(showFeedbackModal({ 
          showModal: true, 
          message: t('Provide an answer to mark as answered'), 
          modalType: 'error', 
          duration: 3000 
        }));
        return;
      }
      setStatus(newStatus);
      try {
        const questionIdentifier = item?.question_id || questionIndex.toString();
        await dispatch(finalizeQuestionnaireItem(matterId, questionIdentifier, answer.trim()));
        onUpdate();
        onClose();
      } catch (error) {
        if(!error) return
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Failed to update status'),
          modalType: 'error',
          duration: 3000
        }));
      }
    } else if (status === 'answered') {
      setStatus('pending');
      try {
        const questionIdentifier = item?.question_id || questionIndex.toString();
        const updatedItem = {
          question,
          potential_answer: answer,
          status: 'pending',
          client_assigned: clientAssigned,
          who_answered: '',
          when_answered: '',
        } as any; 
        await dispatch(updateQuestionnaireItem(matterId, questionIdentifier, updatedItem));
        onUpdate();
        onClose();
      } catch (error) {
        if(!error) return
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Failed to update status'),
          modalType: 'error',
          duration: 3000
        }));
      }
    } else if (status === 'finalized') {
      handleRestore();
    }
  };

  const handleToggleAssign = async () => {
    const newAssigned = !clientAssigned;
    setClientAssigned(newAssigned);
    
    try {
      const questionIdentifier = item?.question_id || questionIndex.toString();
      await dispatch(assignQuestionnaireItem(matterId, questionIdentifier));
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to toggle assignment:', error);
      dispatch(showFeedbackModal({ 
        showModal: true, 
        message: t('Failed to update assignment'), 
        modalType: 'error', 
        duration: 3000 
      }));
    }
  };

  const handleDelete = () => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Question'),
      subMsg: t('This action cannot be undone.'),
      isDest: true,
      icon: <PiTrashLight className='w-4 h-4 text-danger-600' />,
      onConfirm: async () => {
        try {
          const questionIdentifier = item?.question_id || questionIndex.toString();
          await dispatch(removeQuestionnaireItem(matterId, questionIdentifier));
          dispatch(showFeedbackModal({ 
            showModal: true, 
            message: t('Question deleted'), 
            modalType: 'success', 
            duration: 3000 
          }));
          onUpdate();
          onClose();
          dispatch(hideModal())
        } catch (error) {
          console.error('Failed to delete question:', error);
          dispatch(showFeedbackModal({ 
            showModal: true, 
            message: t('Failed to delete question'), 
            modalType: 'error', 
            duration: 3000 
          }));
        }
      },
      onCancel: () => {
        dispatch(hideModal());
      },
      showModal: true
    }));
  };

  useImperativeHandle(ref, () => ({
    handleSave,
    handleToggleStatus,
    handleToggleAssign,
    handleDelete,
    handleRestore, 
  }));

  const getStatusColor = (status: string): BadgeColor => {
    switch (status) {
      case 'finalized': return 'green'; // Completed
      case 'answered': return 'blue';   // Answered (pending finalization)
      case 'pending': return 'yellow';  // Pending
      default: return 'gray';
    }
  };

  const getStatusDisplay = (status: string): string => {
    switch (status) {
      case 'finalized': return t('Completed');
      case 'answered': return t('Answered');
      case 'pending': return t('Pending');
      default: return status;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(getIntlLocale(), {
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const isCompleted = status === 'finalized';
  const isEditable = !isCompleted || isAddMode;
  const isAnswered = status === 'answered';

  const statusConfig = {
    finalized: {
      bg: 'bg-success-50 dark:bg-success-900/20',
      border: 'border-success-200 dark:border-success-800/40',
      icon: <PiCheckCircleLight className="h-4 w-4 text-success-600 dark:text-success-400" />,
      accent: 'text-success-700 dark:text-success-300',
    },
    answered: {
      bg: 'bg-info-50 dark:bg-info-900/20',
      border: 'border-info-200 dark:border-info-800/40',
      icon: <PiChatTextLight className="h-4 w-4 text-info-600 dark:text-info-400" />,
      accent: 'text-info-700 dark:text-info-300',
    },
    pending: {
      bg: 'bg-warning-50 dark:bg-warning-900/20',
      border: 'border-warning-200 dark:border-warning-800/40',
      icon: <PiClockLight className="h-4 w-4 text-warning-600 dark:text-warning-400" />,
      accent: 'text-warning-700 dark:text-warning-300',
    },
  };

  const cfg = statusConfig[status] || statusConfig.pending;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="h-full flex flex-col bg-white dark:bg-gray-900"
    >
      {/* ── Status header ── */}
      <div data-tour-id="qa-panel-status" className={clsx('px-6 py-3 flex items-center justify-between border-b', cfg.bg, cfg.border)}>
        <div className="flex items-center gap-2.5">
          {cfg.icon}
          <span className={clsx('text-sm font-semibold', cfg.accent)}>
            {getStatusDisplay(status)}
          </span>
        </div>
        {!isAddMode && (
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            {t('Q')} #{questionIndex + 1}
          </span>
        )}
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Question section */}
        <div data-tour-id="qa-panel-question" className={clsx(
          'rounded-lg border p-4 space-y-2.5 transition-colors',
          'border-gray-200 dark:border-gray-700/60',
          'bg-gray-50/60 dark:bg-gray-800/40',
        )}>
          <label htmlFor="question-input" className={clsx(FIELD_LABEL_CLASS, 'flex items-center gap-2')}>
            <PiQuestionMarkLight className="h-3.5 w-3.5" />
            {t('Question')}
            {isCompleted && <PiLockSimpleLight className="h-3 w-3 text-gray-400 dark:text-gray-500" />}
          </label>
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('Enter your question here...')}
            rows={3}
            disabled={isCompleted}
            className={clsx(
              'transition-all duration-200 !bg-white dark:!bg-gray-900',
              isCompleted && '!bg-gray-100 dark:!bg-gray-800 cursor-not-allowed opacity-70',
            )}
            id="question-input"
            label=""
          />
        </div>

        {/* Answer section */}
        <div data-tour-id="qa-panel-answer" className={clsx(
          'rounded-lg border p-4 space-y-2.5 transition-colors',
          'border-gray-200 dark:border-gray-700/60',
          'bg-gray-50/60 dark:bg-gray-800/40',
        )}>
          <label htmlFor="answer-input" className={clsx(FIELD_LABEL_CLASS, 'flex items-center gap-2')}>
            <PiFileTextLight className="h-3.5 w-3.5" />
            {t('Answer')}
            {isCompleted && <PiLockSimpleLight className="h-3 w-3 text-gray-400 dark:text-gray-500" />}
          </label>
          <Textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder={t('Provide your answer here...')}
            rows={6}
            disabled={isCompleted}
            className={clsx(
              'transition-all duration-200 !bg-white dark:!bg-gray-900',
              isCompleted && '!bg-gray-100 dark:!bg-gray-800 cursor-not-allowed opacity-70',
            )}
            id="answer-input"
            label=""
          />
        </div>

        {/* Answer details */}
        {item && !isAddMode && item.who_answered && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={clsx(
              'rounded-lg border p-4 space-y-3',
              'border-gray-200 dark:border-gray-700/60',
              'bg-gray-50/60 dark:bg-gray-800/40',
            )}
          >
            <div className={clsx(FIELD_LABEL_CLASS, 'flex items-center gap-2')}>
              <PiUserLight className="h-3.5 w-3.5" />
              {t('Answer Details')}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500 dark:text-gray-400 min-w-[90px]">{t('Answered by')}</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">{item.who_answered}</span>
              </div>
              {item.when_answered && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-500 dark:text-gray-400 min-w-[90px]">{t('Date')}</span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(item.when_answered)}</span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Client assignment */}
        {isLawyer && !isCompleted && !isAddMode && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={clsx(
              'rounded-lg border p-4 space-y-3',
              'border-gray-200 dark:border-gray-700/60',
              'bg-gray-50/60 dark:bg-gray-800/40',
            )}
          >
            <div className={clsx(FIELD_LABEL_CLASS, 'flex items-center gap-2')}>
              <PiUserPlusLight className="h-3.5 w-3.5" />
              {t('Client Assignment')}
            </div>
            <Button
              variant={clientAssigned ? 'primary' : 'outline'}
              size="small"
              onClick={handleToggleAssign}
              className="w-full justify-center"
            >
              {clientAssigned ? t('Unassign from Client') : t('Assign to Client')}
            </Button>
            {isAnswered && (
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {t('This question will be included in the matter context once completed.')}
              </p>
            )}
          </motion.div>
        )}
      </div>

      {/* ── Footer ── */}
      <div data-tour-id="qa-panel-actions" className="px-6 py-4 border-t border-gray-200 dark:border-gray-700/60 bg-gray-50/80 dark:bg-gray-800/40 flex items-center justify-between gap-3">
        {isLawyer && !isAddMode ? (
          <Button
            variant="destructive"
            size="small"
            onClick={handleDelete}
            icon={<PiTrashLight className="h-3.5 w-3.5" />}
          >
            {t('Delete')}
          </Button>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="small"
            onClick={onClose}
          >
            {t('Cancel')}
          </Button>
          {isCompleted ? (
            <Button
              variant="outline"
              size="small"
              onClick={handleRestore}
              icon={<PiArrowLeftLight className="h-3.5 w-3.5" />}
            >
              {t('Restore')}
            </Button>
          ) : (
            <>
              {status === 'answered' && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={async () => {
                    if (!matterId) return;
                    dispatch(showModal({
                      showModal: true,
                      type: 'confirmation',
                      message: t('Mark as Complete?'),
                      subMsg: t('This will finalize this question and mark it as complete.'),
                      onConfirm: async () => {
                        try {
                          const questionIdentifier = item?.question_id || questionIndex.toString();
                          await dispatch(finalizeQuestionnaireItem(matterId, questionIdentifier));
                          dispatch(showFeedbackModal({
                            showModal: true,
                            message: t('Question marked as complete'),
                            modalType: 'success',
                            duration: 3000
                          }));
                          onUpdate();
                          onClose();
                        } catch (error) {
                          dispatch(showFeedbackModal({
                            showModal: true,
                            message: t('Failed to complete question'),
                            modalType: 'error',
                            duration: 3000
                          }));
                        }
                        dispatch(hideModal());
                      },
                      onCancel: () => dispatch(hideModal()),
                      icon: <PiCheckCircleLight className="w-6 h-6 text-success-600" />,
                    }));
                  }}
                  icon={<PiCheckCircleLight className="h-3.5 w-3.5" />}
                >
                  {t('Complete')}
                </Button>
              )}
              <Button
                variant="primary"
                size="small"
                onClick={handleSave}
                disabled={!question.trim() || (status === 'answered' && !answer.trim())}
                icon={<PiPencilSimpleLight className="h-3.5 w-3.5" />}
              >
                {t('Save')}
              </Button>
            </>
          )}
        </div>
      </div>
      <FeatureTour
        steps={questionnairePanelTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </motion.div>
  );
});

export default QuestionnairePanel;
